// WebGL plumbing shared by every scene: the whole picture is one fragment
// shader drawn over a single triangle that covers the canvas.

const VERT = 'attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }';

export class ShaderError extends Error {}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type);
  if (!s) throw new ShaderError('could not create a shader');
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new ShaderError(gl.getShaderInfoLog(s) ?? 'shader failed to compile');
  return s;
}

// Compile and link `frag`, bind the fullscreen triangle, and use the program.
export function fullscreenProgram(gl: WebGLRenderingContext, frag: string): WebGLProgram {
  const prog = gl.createProgram();
  if (!prog) throw new ShaderError('could not create a program');
  gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, frag));
  gl.bindAttribLocation(prog, 0, 'a');
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new ShaderError(gl.getProgramInfoLog(prog) ?? 'program failed to link');
  gl.useProgram(prog);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  return prog;
}

// Uniform locations by name. A uniform the shader doesn't use comes back null,
// which every gl.uniform* call accepts (and ignores).
export function uniforms<K extends string>(gl: WebGLRenderingContext, prog: WebGLProgram, names: readonly K[]): Record<K, WebGLUniformLocation | null> {
  const out = {} as Record<K, WebGLUniformLocation | null>;
  for (const n of names) out[n] = gl.getUniformLocation(prog, n);
  return out;
}

export function drawFullscreen(gl: WebGLRenderingContext): void {
  gl.drawArrays(gl.TRIANGLES, 0, 3);
}
